const SECTION_HEADING_RE = /^(#{1,3}\s*)(?:(?:SECTION|CLAUSE)\s+)?(\d+[A-Za-z]?)\s*[.:\-–—]\s*(.+)/gm;
const SCHEDULE_HEADING_RE = /^(#{1,3}\s*)(?:SCHEDULE)\s+(\d+)\s*[.:\-–—]\s*(.+)/gm;
const EXECUTION_HEADING_RE = /^#{1,3}\s*EXECUTION\b/m;
const TOC_BLOCK_RE = /^(#{1,3}\s*TABLE\s+OF\s+CONTENTS.*)\n([\s\S]*?)(?=\n#{1,3}\s|\n---)/im;

interface SectionBlock {
  heading: string;
  prefix: string;
  number: string;
  title: string;
  startIndex: number;
  endIndex: number;
  normalizedTopic: string;
}

function normalizeTopic(title: string): string {
  return title
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function parseSections(doc: string): SectionBlock[] {
  const blocks: SectionBlock[] = [];
  const allHeadingRe = /^(#{1,3}\s*)(?:(?:SECTION|CLAUSE)\s+)?(\d+[A-Za-z]?)\s*[.:\-–—]\s*(.+)/gm;
  let match: RegExpExecArray | null;

  while ((match = allHeadingRe.exec(doc)) !== null) {
    blocks.push({
      heading: match[0],
      prefix: match[1],
      number: match[2],
      title: match[3].trim(),
      startIndex: match.index,
      endIndex: -1,
      normalizedTopic: normalizeTopic(match[3]),
    });
  }

  const scheduleRe = /^(#{1,3}\s*)(SCHEDULE)\s+(\d+)\s*[.:\-–—]\s*(.+)/gm;
  while ((match = scheduleRe.exec(doc)) !== null) {
    blocks.push({
      heading: match[0],
      prefix: match[1],
      number: `S${match[3]}`,
      title: match[4].trim(),
      startIndex: match.index,
      endIndex: -1,
      normalizedTopic: `schedule_${match[3]}_${normalizeTopic(match[4])}`,
    });
  }

  blocks.sort((a, b) => a.startIndex - b.startIndex);

  for (let i = 0; i < blocks.length; i++) {
    blocks[i].endIndex = i + 1 < blocks.length
      ? blocks[i + 1].startIndex
      : doc.length;
  }

  return blocks;
}

export function removeDuplicateSections(doc: string): string {
  const blocks = parseSections(doc);
  if (blocks.length === 0) return doc;

  const seen = new Map<string, number>();
  const removals: Array<{ start: number; end: number }> = [];

  for (let i = 0; i < blocks.length; i++) {
    const topic = blocks[i].normalizedTopic;
    if (!topic) continue;

    if (seen.has(topic)) {
      removals.push({ start: blocks[i].startIndex, end: blocks[i].endIndex });
    } else {
      seen.set(topic, i);
    }
  }

  if (removals.length === 0) return doc;

  let result = '';
  let cursor = 0;
  for (const { start, end } of removals) {
    result += doc.slice(cursor, start);
    cursor = end;
  }
  result += doc.slice(cursor);

  return result.replace(/\n{3,}/g, '\n\n');
}

export function regenerateTableOfContents(doc: string): string {
  const blocks = parseSections(doc);
  if (blocks.length === 0) return doc;

  const hasExecution = EXECUTION_HEADING_RE.test(doc);

  const tocEntries = blocks.map((b) => {
    const isSchedule = b.number.startsWith('S');
    const label = isSchedule
      ? `Schedule ${b.number.slice(1)}`
      : `Section ${b.number}`;
    return `| ${label} | ${b.title} |`;
  });

  if (hasExecution) {
    tocEntries.push('| Execution | Signatures |');
  }

  const newToc = [
    '| Section | Title |',
    '|---------|-------|',
    ...tocEntries,
  ].join('\n');

  const tocMatch = TOC_BLOCK_RE.exec(doc);
  if (tocMatch) {
    const tocHeading = tocMatch[1];
    const tocStart = tocMatch.index;
    const tocEnd = tocMatch.index + tocMatch[0].length;
    return doc.slice(0, tocStart) + tocHeading + '\n\n' + newToc + '\n' + doc.slice(tocEnd);
  }

  return doc;
}

export function extractSectionHeadings(doc: string): string[] {
  const headings: string[] = [];

  const sectionRe = /^#{1,3}\s*(?:(?:SECTION|CLAUSE)\s+)?(\d+[A-Za-z]?)\s*[.:\-–—]/gm;
  let m: RegExpExecArray | null;
  while ((m = sectionRe.exec(doc)) !== null) {
    headings.push(m[1]);
  }

  const schedRe = /^#{1,3}\s*SCHEDULE\s+(\d+)/gm;
  while ((m = schedRe.exec(doc)) !== null) {
    headings.push(`Schedule ${m[1]}`);
  }

  if (EXECUTION_HEADING_RE.test(doc)) {
    headings.push('EXECUTION');
  }

  return headings;
}

export function buildSectionTracker(
  accumulatedText: string,
  allExpectedSections: string[],
): string {
  const written = extractSectionHeadings(accumulatedText);
  const writtenSet = new Set(written.map(s => s.toLowerCase()));
  const remaining = allExpectedSections.filter(
    s => !writtenSet.has(s.toLowerCase()),
  );

  if (remaining.length === 0 && written.length > 0) {
    return 'All sections have been written. Ensure the EXECUTION section with signature blocks is complete.';
  }

  return `Sections already written: ${written.join(', ') || 'none'}. Sections remaining: ${remaining.join(', ')}.`;
}

export const TENANCY_EXPECTED_SECTIONS = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21',
  'Schedule 1', 'Schedule 2', 'EXECUTION',
];

export const COMMERCIAL_EXPECTED_SECTIONS = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
  'Schedule 1', 'Schedule 2', 'Schedule 3',
  'EXECUTION',
];

export const TENANCY_EXECUTION_FALLBACK = `

## EXECUTION

This agreement has been read and understood by all parties and is signed on the date stated above.

**THE LANDLORD**

Name: [Landlord Name]

Signature: ___________________________

Date: ___________________________

**THE TENANT(S)**

Name: [Tenant Name]

Signature: ___________________________

Date: ___________________________

**THE GUARANTOR**

Name: [Guarantor Name]

Signature: ___________________________

Date: ___________________________

**WITNESS**

Name: ___________________________

Address: ___________________________

Signature: ___________________________

Date: ___________________________
`;

export const COMMERCIAL_EXECUTION_FALLBACK = `

## EXECUTION

This lease has been read and understood by all parties and is executed as a deed on the date stated above.

**THE LANDLORD**

Name: [Landlord Name / Company]

Signature: ___________________________

Position: ___________________________

Date: ___________________________

**THE TENANT**

Name: [Tenant Name / Company]

Signature: ___________________________

Position: ___________________________

Date: ___________________________

**WITNESS**

Name: ___________________________

Address: ___________________________

Signature: ___________________________

Date: ___________________________
`;
